import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useLocation, useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useChangeableItems, getLabelByKey, useSelectedOrganizationId, useOrganizations } from "../../store/useDynamicLabel";

const AddInstitute = () => {
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);
  const organization_id = localStorage.getItem('organization_id')
  const selectedOrganizationId = useSelectedOrganizationId();
  const organizations = useOrganizations();
  const isCorporate =
    organizations.find((org) => org.id === selectedOrganizationId)?.name === "corporate";

  const location = useLocation();

  const { addInstitute, institute_id } = location.state || "";

  const [loading, setLoading] = useState(false);

  const schema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .transform((value) => (typeof value === "string" ? value.replace(/\s+/g, " ") : value))
      .required(`Please enter the ${getLabel("INSTITUTE")} name`)
      .matches(/[A-Za-z]/, `${getLabel("INSTITUTE")} name must contain letters`)
      .min(10, `${getLabel("INSTITUTE")} name must be at least 10 characters`)
      .max(100, `${getLabel("INSTITUTE")} name must not exceed 100 characters`),
    shortname: Yup.string()
      .trim()
      .required(`Please enter the ${getLabel("INSTITUTE")} Display name`)
      .min(2, "Institute display name must be at least 2 characters long.")
      .max(63, "Institute display name must not exceed 63 characters.")
      .matches(
        /^[a-z](?:[a-z0-9-]*[a-z0-9])?$/,
        "Start with a lowercase letter. Use only lowercase letters, numbers, and hyphens. Hyphens cannot be at the beginning or end."),
    contactNumber: Yup.string()
      .min(10, "Atleast 10 numbers is required")
      .max(15, "Contact number must not exceed 15 digits")
      .required("Please enter the contact number"),

    email: Yup.string()
      .trim()
      .required("Please enter your Email Address")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter a valid Email Address"
      )
      .min(5, "Email must be at least 5 characters")
      .max(100, "Email must not exceed 100 characters"),

    status:
      addInstitute === "false" &&
      Yup.string()
        .oneOf(["ACTIVE", "INACTIVE"], "Select status")
        .required("Status is required"),

    address: Yup.string()
      .trim()
      .required("Please enter the address")
      .min(10, "Address must be at least 10 characters long."),

    city: Yup.string()
      .trim()
      .required("Please enter the city")
      .matches(/^[A-Za-z ]+$/, "City name must be at least 2 characters long and contain only letters and spaces.")
      .min(2, "City name must be at least 2 characters long and contain only letters and spaces."),

    state: Yup.string()
      .trim()
      .required("Please enter the state")
      .matches(/^[A-Za-z ]+$/, "State can only contain letters")
      .min(3, "State must be at least 3 characters")
      .max(50, "State must not exceed 50 characters"),

    pincode: Yup.string()
      .trim()
      .required("Please enter the pincode")
      .matches(/^[1-9][0-9]{5}$/, "Enter a valid 6-digit pincode"),
  });

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      shortname: "",
      contactNumber: "",
      email: "",
      status: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      // Off by default: sharing this institute's question banks with every other
      // institute has to be an explicit choice, not something you get by accident.
      shareQuestionBanks: false,
    },
  });
  const fetchById = async () => {
    setLoading(true);
    try {
      const payload = {
        institute_id: institute_id,
      };
      const res = await api.post("ViewInstituteByID", payload);
      if (res?.data?.status === "success") {
        setValue("name", res?.data?.data?.name);
        setValue("contactNumber", res?.data?.data?.contact_phone);
        setValue("email", res?.data?.data?.contact_email);
        setValue("status", res?.data?.data?.status);
        setValue("address", res?.data?.data?.address);
        setValue("city", res?.data?.data?.city);
        setValue("state", res?.data?.data?.state);
        setValue("pincode", res?.data?.data?.pincode);
        setValue("shortname", res?.data?.data?.short_name);
        // Absent on older records — treat "not set" as not shared.
        setValue("shareQuestionBanks", !!res?.data?.data?.access_question_banks);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.log("Error :", error);
      setLoading(false);
    }
  };
  const onSubmit = async (data) => {
    console.log("data :", data);
    setLoading(true);
    try {
      const createpayload = {
        name: data?.name,
        contact_email: data?.email,
        contact_phone: data?.contactNumber,
        status: "ACTIVE",
        address: data?.address,
        city: data?.city,
        state: data?.state,
        pincode: data?.pincode,
        short_name: data?.shortname,
        subdomain: data?.shortname,
        share_question_banks: !!data?.shareQuestionBanks,
        organization_id: selectedOrganizationId || +organization_id
      };
      const updatepayload = {
        institute_id: institute_id,
        name: data?.name,
        contact_email: data?.email,
        contact_phone: data?.contactNumber,
        status: data?.status,
        address: data?.address,
        city: data?.city,
        pincode: data?.pincode,
        state: data?.state,
        short_name: data?.shortname,
        share_question_banks: !!data?.shareQuestionBanks,
        organization_id: selectedOrganizationId || +organization_id
      };
      if (addInstitute === "true") {
        const createres = await api.post("AddInstitute", createpayload);
        if (createres?.data?.status === "success") {
          showSnackbar("success", createres?.data?.message);
          navigate("/institutes");
          reset();
        } else {
          showSnackbar("error", createres?.data?.message);
        }
      } else {
        const updateres = await api.post("UpdateInstitute", updatepayload);
        if (updateres?.data?.status === "success") {
          showSnackbar("success", updateres?.data?.message);
          navigate("/institutes");
          reset();
        } else {
          showSnackbar("error", updateres?.data?.message);
        }
      }
    } catch (error) {
      showSnackbar("error", "Something went wrong!");
      console.log("Error :", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (institute_id) {
      fetchById();
    }
  }, [institute_id]);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {loading ? (
        <OverAllLoader message="Saving institute..." />
      ) : (
        <Box>
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "40px",
              marginBottom: "20px",
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>
          <Box>
            <Grid
              sx={{
                pb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  backgroundColor: "#1c97e3",
                  p: 1,
                  border: "1px",
                  borderRadius: "10px",
                }}
              >
                <BusinessIcon sx={{ color: "white", fontSize: "35px" }} />
              </Grid>
              <Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {addInstitute === "true" ? "Add" : "Edit"} {" "} {getLabel("INSTITUTE")}
                </Typography>
              </Grid>
            </Grid>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 1,
                  p: 2,
                }}
              >
                {/* Question bank sharing — sits above Basic Information because it is an
                    institute-wide policy choice, not one of its detail fields. */}
                <Controller
                  name="shareQuestionBanks"
                  control={control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        p: 2,
                        mt: 2,
                        mb: 2,
                        borderRadius: "12px",
                        border: field.value ? "1px solid #1c97e3" : "1px solid #d6dde6",
                        backgroundColor: field.value ? "#f2f9fe" : "transparent",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <ShareIcon sx={{ color: "#1c97e3", fontSize: "26px" }} />
                        <Box>
                          <Typography
                            component="label"
                            htmlFor="share-question-banks"
                            sx={{ fontWeight: 600, cursor: "pointer" }}
                          >
                            Do you want to access our question bank repository?
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#8E9EBC" }}>
                            {isCorporate
                              ? "Every other organization can view this organization's question banks."
                              : "Every other institute can view this institute's question banks."}
                          </Typography>
                        </Box>
                      </Box>
                      <Switch
                        id="share-question-banks"
                        checked={!!field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                        inputProps={{ "aria-label": "Share this institute's question banks with all institutes" }}
                      />
                    </Box>
                  )}
                />

                <Grid>
                  <Grid
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Grid>
                      <BusinessIcon
                        sx={{
                          color: "#1c97e3",
                          fontSize: "30px",
                          fontWeight: "bold",
                        }}
                      />
                    </Grid>
                    <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                      Basic Information
                    </Typography>
                  </Grid>
                  <Grid sx={{ display: "flex", gap: 3 }}>
                    <Grid sx={{ width: "100%" }}>
                      {/* Name Field */}
                      <Typography>
                        {getLabel("INSTITUTE")} Name <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            // Block leading spaces and collapse repeated spaces while typing
                            onChange={(e) =>
                              field.onChange(
                                e.target.value.replace(/^\s+/, "").replace(/\s{2,}/g, " ")
                              )
                            }
                            fullWidth
                            placeholder={`Enter ${getLabel("INSTITUTE")} Name`}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid sx={{ width: "100%" }}>
                      {/* Name Field */}
                      <Typography>
                        Display Name{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="shortname"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            placeholder={`${getLabel('INSTITUTE')} Short Name eg.) ABC`}
                            error={!!errors.shortname}
                            helperText={errors.shortname?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {addInstitute === "false" && (
                      <Grid sx={{ width: "100%" }}>
                        {/* Status Dropdown */}
                        <Typography>
                          Status <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <Controller
                          name="status"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              error={!!errors.status}
                              helperText={errors.status?.message}
                              SelectProps={{ displayEmpty: true }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "white",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#d1d1d1",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#1c97e3",
                                  },
                                },
                              }}
                            >
                              <MenuItem
                                value=""
                                disabled
                                sx={{ color: "inherit" }}
                              >
                                Select status
                              </MenuItem>
                              <MenuItem value="ACTIVE">Active</MenuItem>
                              <MenuItem value="INACTIVE">Inactive</MenuItem>
                            </TextField>
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid>
                  <Grid
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Grid>
                      <MailOutlineIcon
                        sx={{
                          color: "#1c97e3",
                          fontSize: "30px",
                          //   fontWeight: "bold",
                        }}
                      />
                    </Grid>
                    <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                      Contact Information
                    </Typography>
                  </Grid>
                  <Grid sx={{ display: "flex", gap: 3 }}>
                    <Grid sx={{ width: "100%" }}>
                      {/* Contact Number */}
                      <Typography>
                        Contact Number <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="contactNumber"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            fullWidth
                            placeholder="Contact Number"
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(
                                /\D/g,
                                ""
                              );
                              field.onChange(e);
                            }}
                            error={!!errors.contactNumber}
                            helperText={
                              errors.contactNumber
                                ? errors.contactNumber.message
                                : ""
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                {
                                  WebkitAppearance: "none",
                                  margin: 0,
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid sx={{ width: "100%" }}>
                      {/* Email Field */}
                      <Typography>
                        Email <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            placeholder="Email"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid>
                  <Grid
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Grid>
                      <LocationOnIcon
                        sx={{
                          color: "#1c97e3",
                          fontSize: "30px",
                          //   fontWeight: "bold",
                        }}
                      />
                    </Grid>
                    <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                      Address Information
                    </Typography>
                  </Grid>
                  <Grid sx={{ display: "flex", gap: 3, mb: 1 }}>
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        Address <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="address"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            // Block leading spaces and collapse repeated spaces while typing (line breaks kept)
                            onChange={(e) =>
                              field.onChange(
                                e.target.value.replace(/^\s+/, "").replace(/[ \t]{2,}/g, " ")
                              )
                            }
                            fullWidth
                            placeholder="Address"
                            error={!!errors.address}
                            helperText={errors.address?.message}
                            multiline
                            minRows={4}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                alignItems: "flex-start",
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                              "& .MuiInputBase-input": {
                                textAlign: "left",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid sx={{ display: "flex", gap: 3 }}>
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        City <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="city"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            placeholder="City"
                            error={!!errors.city}
                            helperText={errors.city?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        State <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="state"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            placeholder="State"
                            error={!!errors.state}
                            helperText={errors.state?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        Pincode <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="pincode"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="text"
                            fullWidth
                            placeholder="Pincode"
                            error={!!errors.pincode}
                            helperText={errors.pincode?.message}
                            inputProps={{ maxLength: 6 }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2, // spacing between buttons
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 3,
                      py: 1,
                      fontWeight: 500,
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      "&:hover": {
                        boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
                      },
                    }}
                  >
                    {addInstitute === "true" ? "Add" : "Update"} {`${getLabel('INSTITUTE')}`}
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddInstitute;
